// ─────────────────────────────────────────────────────────────────────────────
// AXIS360 CAREER INTEREST PROFILE — Question Bank
// 48 original Likert items (0=Strongly Dislike → 4=Strongly Enjoy), 8 per
// dimension across Holland's (1959, 1997) RIASEC model of vocational
// interest types: Realistic, Investigative, Artistic, Social, Enterprising,
// Conventional.
//
// Important note on originality: this instrument is built entirely from
// scratch on Holland's public-domain academic theory. It is NOT a
// reproduction of, and shares no item content with, any commercially
// licensed interest inventory (e.g. the Strong Interest Inventory, published
// by The Myers-Briggs Company). The RIASEC framework itself is standard,
// widely-taught vocational psychology — not proprietary to any single
// commercial product built on top of it.
// Science: Holland, J.L. (1959). A theory of vocational choice. Journal of
// Counseling Psychology, 6(1), 35-45 · Holland, J.L. (1997). Making
// Vocational Choices (3rd ed.). Psychological Assessment Resources.
// ─────────────────────────────────────────────────────────────────────────────

export const RIASEC_ITEMS = [
  // ── REALISTIC — hands-on, mechanical, physical, tool-using (items 0-7) ──────
  { id:"ri01", dim:"R", q:"Assembling or repairing something with your hands." },
  { id:"ri02", dim:"R", q:"Operating machinery, vehicles, or power tools." },
  { id:"ri03", dim:"R", q:"Working outdoors rather than at a desk." },
  { id:"ri04", dim:"R", q:"Building something physical from a set of plans or instructions." },
  { id:"ri05", dim:"R", q:"Troubleshooting why a physical device or system has stopped working." },
  { id:"ri06", dim:"R", q:"Working with your hands on a task with a clear, tangible end result." },
  { id:"ri07", dim:"R", q:"Physical or technical work that most people would consider hands-on labour." },
  { id:"ri08", dim:"R", q:"Learning to operate a new piece of equipment or technical system." },

  // ── INVESTIGATIVE — analytical, scientific, research-oriented (items 8-15) ──
  { id:"ri09", dim:"I", q:"Investigating a complex problem with no obvious answer." },
  { id:"ri10", dim:"I", q:"Reading in depth about a scientific or technical topic out of pure curiosity." },
  { id:"ri11", dim:"I", q:"Designing an experiment or study to test a specific hypothesis." },
  { id:"ri12", dim:"I", q:"Analysing data to find a pattern others might have missed." },
  { id:"ri13", dim:"I", q:"Working through a difficult puzzle or logic problem for its own sake." },
  { id:"ri14", dim:"I", q:"Researching a topic thoroughly before forming an opinion on it." },
  { id:"ri15", dim:"I", q:"Understanding the underlying theory behind how something works, not just how to use it." },
  { id:"ri16", dim:"I", q:"Spending long, uninterrupted periods thinking through a technical or abstract problem." },

  // ── ARTISTIC — creative, expressive, unstructured, aesthetic (items 16-23) ──
  { id:"ri17", dim:"A", q:"Creating something original — writing, music, visual art, or design." },
  { id:"ri18", dim:"A", q:"Working on a project with no fixed process, where you decide the approach as you go." },
  { id:"ri19", dim:"A", q:"Expressing an idea through an unconventional or artistic format." },
  { id:"ri20", dim:"A", q:"Redesigning something (a space, a document, a product) to be more visually appealing." },
  { id:"ri21", dim:"A", q:"Brainstorming freely without worrying about whether every idea is practical." },
  { id:"ri22", dim:"A", q:"Work that rewards originality over following an established procedure." },
  { id:"ri23", dim:"A", q:"Performing, presenting, or creating something for others to experience." },
  { id:"ri24", dim:"A", q:"Having significant creative freedom in how you approach a task." },

  // ── SOCIAL — helping, teaching, counselling, interpersonal (items 24-31) ────
  { id:"ri25", dim:"S", q:"Helping someone work through a personal or professional problem." },
  { id:"ri26", dim:"S", q:"Teaching or explaining a concept to someone until it clicks for them." },
  { id:"ri27", dim:"S", q:"Spending most of your working day interacting directly with people, not tasks or data." },
  { id:"ri28", dim:"S", q:"Mentoring someone earlier in their career or studies." },
  { id:"ri29", dim:"S", q:"Mediating a disagreement between two people to find common ground." },
  { id:"ri30", dim:"S", q:"Volunteering time to support a cause or community you care about." },
  { id:"ri31", dim:"S", q:"Noticing when someone is struggling and checking in on them, even if it's not your job to." },
  { id:"ri32", dim:"S", q:"Working in a role centred on other people's wellbeing or development." },

  // ── ENTERPRISING — persuading, leading, selling, business (items 32-39) ─────
  { id:"ri33", dim:"E", q:"Persuading a person or group to support an idea you believe in." },
  { id:"ri34", dim:"E", q:"Leading a team or project toward an ambitious goal." },
  { id:"ri35", dim:"E", q:"Negotiating a deal, price, or agreement to a favourable outcome." },
  { id:"ri36", dim:"E", q:"Starting something new — a project, a venture, an initiative — from scratch." },
  { id:"ri37", dim:"E", q:"Competing to win, whether in business, sport, or another arena." },
  { id:"ri38", dim:"E", q:"Taking the lead in a group when no one else has stepped up." },
  { id:"ri39", dim:"E", q:"Pitching an idea or product to an audience that needs convincing." },
  { id:"ri40", dim:"E", q:"Making decisions that carry real financial or strategic consequences." },

  // ── CONVENTIONAL — organising, data, structure, administration (items 40-47) ─
  { id:"ri41", dim:"C", q:"Organising information into a clear, structured system." },
  { id:"ri42", dim:"C", q:"Following an established, well-defined process precisely." },
  { id:"ri43", dim:"C", q:"Maintaining detailed, accurate records over a long period." },
  { id:"ri44", dim:"C", q:"Working within clear rules, procedures, and expectations rather than ambiguity." },
  { id:"ri45", dim:"C", q:"Reviewing a document or dataset closely to catch every error." },
  { id:"ri46", dim:"C", q:"Managing schedules, budgets, or logistics in careful detail." },
  { id:"ri47", dim:"C", q:"Bringing order to a disorganised process or system." },
  { id:"ri48", dim:"C", q:"Work where accuracy and consistency matter more than novelty." },
];

export const RIASEC_DIMS = {
  R: { id:"R", label:"Realistic",     icon:"🔧", color:"#78716c",
       tagline:"Hands-on, mechanical, physical work with tangible results.",
       themes:["Skilled trades & technical operations","Engineering & applied technology","Agriculture, construction & outdoor work"] },
  I: { id:"I", label:"Investigative", icon:"🔬", color:"#1d4ed8",
       tagline:"Analytical, scientific inquiry and complex problem-solving.",
       themes:["Research & scientific analysis","Data science & technical strategy","Diagnostics & systematic investigation"] },
  A: { id:"A", label:"Artistic",      icon:"🎨", color:"#c026d3",
       tagline:"Creative expression, originality and unstructured work.",
       themes:["Design & creative direction","Writing, content & media","Product and experience design"] },
  S: { id:"S", label:"Social",        icon:"🤝", color:"#059669",
       tagline:"Helping, teaching and working closely with people.",
       themes:["Learning & development","People & culture, coaching","Customer success & community roles"] },
  E: { id:"E", label:"Enterprising",  icon:"📈", color:"#d97706",
       tagline:"Leading, persuading and driving business outcomes.",
       themes:["Sales & business development","General management & leadership","Entrepreneurship & venture-building"] },
  C: { id:"C", label:"Conventional",  icon:"📋", color:"#0891b2",
       tagline:"Organising, structuring and maintaining accuracy in detail.",
       themes:["Operations & process management","Finance & accounting","Compliance, quality & administration"] },
};

// Array form (parallels EQ's DOMAINS convention) — used by the generic
// InstrumentSurvey completion screen, which expects an array of
// {id, name, shortName, icon, color, bg}, not the keyed object above.
export const RIASEC_SCALES = Object.values(RIASEC_DIMS).map(d => ({
  id: d.id, name: d.label, shortName: d.label, icon: d.icon, color: d.color, bg: d.color + "18",
}));
